/**
 * Global auth-error detection and reporting.
 *
 * Any API call (Lovable Cloud/PostgREST, edge functions, fetch) that comes back
 * 401 or 403 while the user is navigating should surface one friendly screen
 * instead of a raw toast or a blank panel. This module classifies those errors
 * and broadcasts them so a single app-level gate can render the right surface.
 */

export type AuthErrorKind = "unauthenticated" | "forbidden";

export interface AuthErrorEvent {
  kind: AuthErrorKind;
  /** Path the user was on when the failure happened. */
  path: string;
}

type Listener = (event: AuthErrorEvent) => void;

const listeners = new Set<Listener>();

/** PostgREST / Supabase codes that mean "no permission" rather than "bad query". */
const FORBIDDEN_CODES = new Set(["42501", "PGRST301", "PGRST302"]);
const UNAUTHENTICATED_CODES = new Set([
  "401",
  "PGRST303",
  "invalid_jwt",
  "bad_jwt",
  "session_not_found",
  "refresh_token_not_found",
]);

function readStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as Record<string, unknown>;
  const candidates = [e.status, e.statusCode, e.httpStatus, (e.context as Record<string, unknown> | undefined)?.status];
  for (const c of candidates) {
    const n = typeof c === "string" ? Number(c) : c;
    if (typeof n === "number" && Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Classify an unknown thrown value as a 401/403 auth error, or null. */
export function classifyAuthError(error: unknown): AuthErrorKind | null {
  if (!error || typeof error !== "object") return null;
  const e = error as Record<string, unknown>;

  const status = readStatus(error);
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";

  const code = typeof e.code === "string" ? e.code : "";
  if (UNAUTHENTICATED_CODES.has(code)) return "unauthenticated";
  if (FORBIDDEN_CODES.has(code)) return "forbidden";

  const message = typeof e.message === "string" ? e.message : "";
  if (/jwt expired|invalid (jwt|token)|no api key|missing authorization/i.test(message)) {
    return "unauthenticated";
  }
  if (/row-level security|permission denied|not authorized|unauthorized/i.test(message)) {
    // "unauthorized" wording from edge functions usually means a missing session.
    return /unauthorized/i.test(message) && !/permission denied|row-level security/i.test(message)
      ? "unauthenticated"
      : "forbidden";
  }

  return null;
}

/**
 * Full-page AuthErrorGate must not replace first-run onboarding.
 *
 * `/app/home` is allowed for every signed-in learner. Nested 403s (RLS on a
 * dashboard query before `learner_profiles.primary_goal` exists) used to paint
 * **You don't have access to this** and race `LearnerHome`'s `/welcome`
 * redirect (**K92**). `/welcome` is the goal picker — a forbidden gate there
 * would trap the same users.
 *
 * Unauthenticated (401) still shows the sign-in screen on these paths.
 */
export function shouldSuppressAuthErrorGate(event: AuthErrorEvent): boolean {
  if (event.kind !== "forbidden") return false;
  const path = event.path.split("?")[0].replace(/\/+$/, "") || "/";
  return path === "/app/home" || path === "/welcome";
}

/** Subscribe to global auth failures. Returns an unsubscribe function. */
export function onAuthError(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Broadcast an auth failure. No-ops when the error is not a 401/403. */
export function reportAuthError(error: unknown): AuthErrorKind | null {
  const kind = classifyAuthError(error);
  if (!kind) return null;
  const path =
    typeof window === "undefined" ? "/" : window.location.pathname + window.location.search;
  listeners.forEach((l) => l({ kind, path }));
  return kind;
}
